import { useMemo } from "react";

import isEqual from "lodash.isequal";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

import { useAccount } from "../../entities";
import { FREQUENCIES } from "../../../../Recurrence";
import {
  useMaximumAvailablePullAmount,
  useMaximumAvailablePushAmount,
} from "../../entities/limits";

import { TransferForm } from "./TransferFunds.form";

import type { RootState } from "../../entities";

export const useSelectedSourceAccount = () => {
  const {
    values: { fromAccount },
  } = TransferForm.useForm();
  return useAccount(fromAccount);
};

export const useSelectedDestinationAccount = () => {
  const {
    values: { toAccount },
  } = TransferForm.useForm();
  return useAccount(toAccount);
};

export const useIsInstantEligible = () => {
  return useSelector(
    (state: RootState) => state.transfers.destinationInstantEligible,
  );
};

export const useIsScheduledTransfer = () => {
  const {
    values: { frequency, date, instant },
  } = TransferForm.useForm();
  return useMemo(() => {
    const today = DateTime.now().startOf("day");
    const dtDate = date && DateTime.fromISO(date);
    let frequencyVal;
    // atlas and azul frequency selector components return key and value of FREQUENCIES in to in their onChange functions
    // This mapping logic is to support both formats so the same hook can be re-used in both.
    if (frequency && Object.keys(FREQUENCIES).includes(frequency)) {
      frequencyVal = FREQUENCIES[frequency as "ONCE"];
    } else {
      frequencyVal = frequency;
    }
    return !!(
      !instant &&
      dtDate &&
      (frequencyVal !== FREQUENCIES.ONCE || !dtDate.equals(today))
    );
  }, [frequency, date, instant]);
};

export const useSubmittedTransferState = () => {
  return useSelector((state: RootState) => state.transfers.transferState);
};
export const useSubmittedTransferId = () => {
  return useSelector(
    (state: RootState) => state.transfers.paymentIdentification,
  );
};
export const useFedNowPayment = () => {
  return useSelector(
    (state: RootState) => state.transfers.fednowPayment,
    isEqual,
  );
};

export const useIsExternalTransfer = () => {
  const {
    values: { toAccount, fromAccount },
  } = TransferForm.useForm();
  const source = useAccount(fromAccount);
  const destination = useAccount(toAccount);
  return (
    (source && source.isExternal()) || (destination && destination.isExternal())
  );
};

export const useTransferFees = () => {
  return useSelector((state: RootState) => state.transfers.fees);
};

export const useCalculateInstantTransferFee = () => {
  const fees = useTransferFees();
  const {
    values: { amount },
  } = TransferForm.useForm();
  return useMemo(() => {
    if (!fees) {
      return 0;
    }
    switch (fees.kind) {
      case "free":
        return 0;
      case "fixed":
        return fees.max as number;
      case "percent": {
        if (
          fees.min === undefined ||
          fees.max === undefined ||
          fees.percent === undefined
        ) {
          return 0;
        }
        const percentValue = (fees.percent * amount) / 100;
        if (percentValue < fees.min) {
          return fees.min;
        }
        if (percentValue > fees.max) {
          return fees.max;
        }
        return percentValue;
      }
      default:
        return 0;
    }
  }, [fees, amount]);
};

export const useReceiptReady = () => {
  const {
    values: { instant },
  } = TransferForm.useForm();
  const sumbissionState = useSubmittedTransferState();
  const fedNowPayment = useFedNowPayment();
  return useMemo(() => {
    if (!instant) {
      return sumbissionState !== undefined;
    }
    if (instant && sumbissionState && sumbissionState !== "processed") {
      return true;
    }
    return (
      fedNowPayment !== undefined && fedNowPayment.state !== "awaiting_response"
    );
  }, [instant, fedNowPayment, sumbissionState]);
};

export const useBasicBalanceLimitCheck = () => {
  const source = useSelectedSourceAccount();
  const destination = useSelectedDestinationAccount();
  const {
    values: { amount },
  } = TransferForm.useForm();
  const isExternalTransfer = useIsExternalTransfer();
  const maxPush = useMaximumAvailablePushAmount();
  const maxPull = useMaximumAvailablePullAmount();
  return useMemo(() => {
    if (!source || !destination) {
      return false;
    }
    const floatAmount = amount / 100;
    if (!isExternalTransfer) {
      return true;
    }
    const isPush = destination.source === "external";
    if (isPush) {
      return floatAmount <= maxPush;
    }
    return floatAmount <= maxPull;
  }, [source, destination, amount, isExternalTransfer, maxPull, maxPush]);
};
